import { call, put, select, debounce } from "redux-saga/effects";
import actions from "../actions";
import { fetchSessionStatus } from "../api/auth";
import { getIsSigningOut, getSignedinState } from "../selectors";
import { redirectToAuthPortal, isPublicAuthPath } from "../utils/authPortal";
import { hasSessionCookie } from "../utils/sessionCookie";

export function* authorizeUser() {
  try {
    const isSigningOut = yield select(getIsSigningOut);
    const signedInState = yield select(getSignedinState);

    // Skipping authorization check - Logout in progress or explicitly signed out
    if (isSigningOut || signedInState === false) return;

    // No session cookie → skip the /auth/me call entirely. After logout (or an
    // expired/absent session) the cookie is already gone, so redirect to the
    // portal immediately instead of leaving the app in a loading state until
    // the /auth/me round-trip comes back unauthenticated.
    if (!hasSessionCookie() && !isPublicAuthPath()) {
      yield put(actions.setSignedinState(false));
      redirectToAuthPortal();
      return;
    }

    // Validate the cookie session via /auth/me (no token handling on the FE).
    const { result: user, unauthorized } = yield call(fetchSessionStatus);

    if (!user) {
      // Only a confirmed-rejected session (401) signs the user out. A
      // transient failure (network, 5xx) leaves the current bootstrap state
      // intact instead of bouncing a still-validly-cookied user to the
      // portal — that cookie is the source of truth, not this one failed call.
      if (!unauthorized) return;

      yield put(actions.setSignedinState(false));
      // Public in-app routes (invitation/setup/force-password) must not bounce
      // to the portal; everything else does.
      if (!isPublicAuthPath()) {
        redirectToAuthPortal();
      }
      return;
    }

    yield put(actions.setBasicDetails(user));
    yield put(actions.getUser());
    yield put(actions.setSignedinState(true));

    // The session is valid but the user must rotate their password before
    // continuing (e.g. admin-forced reset / never-set temp password). /auth/me
    // surfaces this as `force_password_change`; set the existing redux flag so
    // ForcePasswordChangeGuard routes them to the force-password-change screen.
    if (user.session?.force_password_change) {
      yield put(actions.setUserAttributes({ email: user.email, username: user.email }));
      yield put(actions.forcePasswordChange({}));
    }
  } catch (e) {
    console.log(e);
    yield put(actions.setSignedinState(false));
  }
}

export default function* authSaga() {
  yield debounce(500, actions.CHECK_AUTH_STATE, authorizeUser);
}
